import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import _ from "lodash";
import { Box, Button } from "@mui/material";
import { InlineIcon } from "@iconify/react";
import { MyTimeExaminerDahshboardRoleFilter } from "my-time/components/examiner-dashboard-role-filter";
import { MyTimePageHeader } from "my-time/components/page-header";
import { MyTimeUserApprovalDialog } from "my-time/components";
import { useMyTimeAdmission, useMyTimeRecordingPeriod } from "my-time/hooks";
import {
  myTimeDashboardListTableDef,
  myTimeDashboardListTableDefIsMobile,
  myTimeDashboardListTableDefExaminerAuthorization,
  myTimeDashboardListTableDefViewer
} from "my-time/configs/table-defs";
import {
  CREATE_ALL_APPROVAL_BY_EXAMINER,
  CREATE_APPROVAL_BY_EXAMINER,
  REMOVE_APPROVAL_BY_EXAMINER,
  CREATE_AUTHORIZATION_BY_EXAMINER,
  CREATE_ALL_AUTHORIZATION_BY_EXAMINER,
} from "my-time/graphql/mutations";
import { useMyTimeClosingPage } from "my-time/hooks/use-closing-page";
import useMyTimeDashboardList from "my-time/hooks/use-dashboard-list";
import {
  OcCard,
  OcConfirmationDialog,
  OcDataTable,
  OcPageNameTitle,
} from "oc/components";
import { initialTableData } from "oc/configs/initial-table-data";
import {
  useOcTableState,
  useOcCurrentUserState,
  useOcProperty,
  useOcRoleFilters,
  useOcSnackbar,
} from "oc/hooks";
import checkAll from "@iconify/icons-mdi/check-all";

interface SelectRowProps {
  action: "close" | string;
  row: {
    contractCode: string;
  };
  _id?: string | number;
}

export const MyTimeExaminerDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const { date, formattedDate, isMobile } = useMyTimeClosingPage();
  const { contractCode: contract } = useOcCurrentUserState();
  const product = "MyTime";
  const role = "examiner";

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showAuthorizationConfirmDialog, setShowAuthorizationConfirmDialog] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState<string | number | undefined>(undefined);
  const [showViewingDialog, setShowViewingDialog] = useState(false);
  const [viewingContractCode, setViewingContractCode] = useState<string>("");
  const [selectedUserRow, setSelectedUserRow] = useState<any>(null);

  const { setOpenMessage } = useOcSnackbar();

  const recordingPeriod = useMyTimeRecordingPeriod();
  const { data: viewingData, loading: viewingLoading } = useMyTimeAdmission({
    contractCode: viewingContractCode || "",
    year: recordingPeriod.year,
    month: recordingPeriod.month,
    changeMyTimeEnabled: true,
    recordingPeriod,
    isApproved: false,
  });

  const tableId = "employeeAdmissionsTableData";
  const [tableData, setTableData] = useOcTableState(tableId, {
    ...initialTableData,
    sortField: "pipedName",
    pageSize: 5,
  });
  const [prevTableData, setPrevTableData] = useOcTableState(`${tableId}Prev`, {
    ...initialTableData,
    sortField: "pipedName",
    pageSize: 5,
  });

  const roleFilterId = "dashboardFilter";
  const { roleFilters } = useOcRoleFilters({
    contract,
    product,
    role,
    id: roleFilterId,
  });

  const {
    data: approveData,
  } = useOcProperty({
    resource: "myTime",
    key: "userApprovalEnabled",
  });

  const { data: examinerApprovalData } = useOcProperty({
    resource: "myTime",
    key: "examinerApprovalEnabled",
  });

  const {
    loadQuery,
    data: list,
    loading,
    error,
  } = useMyTimeDashboardList({
    roleFilters,
    tableData,
    role,
    date: date
  });

  const filteredList = list
    .filter((item) => !item.isAcknowledged || !item.isApproved)
    .map((item) => ({
      ...item,
      contractCode: (item as any).contractCode,
    }));

  const data = {
    rows: list || [],
    total: list?.length || 0,
  };

  const [createApproval, { loading: createApprovalLoading }] = useMutation(CREATE_APPROVAL_BY_EXAMINER, {
    onCompleted: (data, options) => {
      setOpenMessage({
        message: t("MY_TIME_FORCE_APPROVAL_SUCCESS"),
        type: "success",
      });
      loadQuery();
      if (selectedUserRow && selectedUserRow.contractCode === options?.variables?.contractCode) {
        setSelectedUserRow({ ...selectedUserRow, isApproved: true, isAcknowledged: true });
      }
    },
    onError: () => {
      setOpenMessage({
        message: t("MY_TIME_FORCE_APPROVAL_ERROR"),
        type: "error",
      });
    },
  });

  const [removeApproval, { loading: removeApprovalLoading }] = useMutation(REMOVE_APPROVAL_BY_EXAMINER, {
    onCompleted: (data, options) => {
      setOpenMessage({
        message: t("MY_TIME_FORCE_APPROVAL_REMOVE_SUCCESS"),
        type: "success",
      });
      loadQuery();
      if (selectedUserRow && selectedUserRow.contractCode === options?.variables?.contractCode) {
        setSelectedUserRow({ ...selectedUserRow, isApproved: false, isAcknowledged: false, isAuthorized: false, authorizedAt: null });
      }
    },
    onError: () => {
      setOpenMessage({
        message: t("MY_TIME_FORCE_APPROVAL_REMOVE_ERROR"),
        type: "error",
      });
    },
  });

  const [createAllApproval, { loading: createAllApprovalLoading }] = useMutation(CREATE_ALL_APPROVAL_BY_EXAMINER, {
    onCompleted: () => {
      setOpenMessage({
        message: t("MY_TIME_FORCE_APPROVAL_ALL_SUCCESS"),
        type: "success",
      });
      loadQuery();
    },
    onError: () => {
      setOpenMessage({
        message: t("MY_TIME_FORCE_APPROVAL_ALL_ERROR"),
        type: "error",
      });
    },
  });

  const [createAuthorization, { loading: createAuthorizationLoading }] = useMutation(CREATE_AUTHORIZATION_BY_EXAMINER, {
    onCompleted: (data, options) => {
      setOpenMessage({
        message: t("OC_OPERATION_SUCCESSFUL"),
        type: "success",
      });
      loadQuery();
      if (selectedUserRow && selectedUserRow.contractCode === options?.variables?.contractCode) {
        setSelectedUserRow({ ...selectedUserRow, isAuthorized: true });
      }
      setShowViewingDialog(false);
    },
    onError: () => {
      setOpenMessage({
        message: t("OC_OPERATION_UNSUCCESSFUL"),
        type: "error",
      });
    },
  });

  const [createAllAuthorization, { loading: createAllAuthorizationLoading }] = useMutation(CREATE_ALL_AUTHORIZATION_BY_EXAMINER, {
    onCompleted: () => {
      setOpenMessage({
        message: t("OC_OPERATION_SUCCESSFUL"),
        type: "success",
      });
      loadQuery();
    },
    onError: () => {
      setOpenMessage({
        message: t("OC_OPERATION_UNSUCCESSFUL"),
        type: "error",
      });
    },
  });

  const handleButtonClick = () => {
    const contractCodes = filteredList.map((item) => item.contractCode);
    createAllApproval({
      variables: {
        contractCodes,
      },
    });
    setShowConfirmDialog(false);
  };

  const handleAuthorizationButtonClick = () => {
    const contractCodes = list.map((item) => (item as any).contractCode);
    createAllAuthorization({
      variables: {
        contractCodes,
      },
    });
    setShowAuthorizationConfirmDialog(false);
  };

  const originalonSelectRow = (props: SelectRowProps) => {
    const { action, row } = props;
    if (action === "close") {
      createApproval({
        variables: {
          contractCode: row.contractCode,
        },
      });
    } else if (action === "authorize") {
      createAuthorization({
        variables: {
          contractCode: row.contractCode,
        },
      });
    } else if (action === "view") {
      setViewingContractCode(row.contractCode);
      setSelectedUserRow(row);
      setShowViewingDialog(true);
    } else {
      removeApproval({
        variables: {
          contractCode: row.contractCode,
        },
      });
    }
  };

  const handleSelectRow = (props: SelectRowProps): void => {
    if (isMobile) {
      setSelectedRowId(selectedRowId === props._id ? undefined : props._id);
    } else {
      originalonSelectRow(props);
    }
  };

  const handleDialogCancel = () => {
    setShowConfirmDialog(false);
  };

  const handleAuthorizationDialogCancel = () => {
    setShowAuthorizationConfirmDialog(false);
  };

  const handleTableDataChange = (state: typeof initialTableData) => {
    const keys = Object.keys(state).toString();
    const newTableData = { ...tableData, ...state };
    setTableData(newTableData);

    // Don't reload for these cases - they will be handled by useEffect or are not relevant
    if (
      keys === "searchField" ||
      (keys === "searchText" && state?.searchText && state.searchText.length < 3)
    ) {
      return;
    }
  };

  const isTabledataChanged = !_.isEqual(tableData, prevTableData);
  useEffect(() => {
    if (isTabledataChanged) {
      loadQuery();
      setPrevTableData(tableData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTabledataChanged]);

  useEffect(() => {
    loadQuery();
    setPrevTableData(tableData);
  }, []); // eslint-disable-line

  const disabledForceButton = filteredList.length === 0;
  const unAuthorizedWorkdays = list.filter((item) => !(item as any).isAuthorized);
  const disabledForceAuthorizationButton = unAuthorizedWorkdays.length === 0;

  const selectedTableDef = useMemo(() => {
    const isApproveAllEnabled = approveData === "true";
    const isExaminerApprovalEnabled = examinerApprovalData === "true";

    if (isApproveAllEnabled && isExaminerApprovalEnabled) {
      return myTimeDashboardListTableDefExaminerAuthorization;
    } else if (isApproveAllEnabled && !isExaminerApprovalEnabled) {
      return myTimeDashboardListTableDef;
    } else {
      return myTimeDashboardListTableDefViewer;
    }
  }, [approveData, examinerApprovalData]);

  const canExaminerApprove = approveData === "true" && examinerApprovalData === "true";

  const tableDef = isMobile
    ? myTimeDashboardListTableDefIsMobile
    : selectedTableDef;

  const filteredTableDef = useMemo(() => ({
    id: selectedTableDef?.id,
    columns: selectedTableDef?.columns?.filter(
      (column) =>
        !myTimeDashboardListTableDefIsMobile?.columns?.some(
          (mobileColumn) => mobileColumn?.id === column?.id
        )
    ),
  }), [selectedTableDef]);

  return (
    <Box>
      <OcConfirmationDialog
        showDialog={showConfirmDialog}
        onClose={handleButtonClick}
        onCancel={handleDialogCancel}
        title={t("MY_TIME_CONFIRM_CLOSE_MONTH_TITLE")}
        texts={[
          t("MY_TIME_CONFIRM_GROUP_CLOSE_MONTH_TEXT"),
          t("MY_TIME_CONFIRM_GROUP_CLOSE_MONTH_TEXT_RESPONSIBILITY"),
        ]}
        okButtonText={t("MY_TIME_CLOSE_MONTH")}
        cancelButtonText={t("OC_CANCEL")}
      />
      <OcConfirmationDialog
        showDialog={showAuthorizationConfirmDialog}
        onClose={handleAuthorizationButtonClick}
        onCancel={handleAuthorizationDialogCancel}
        title={t("MY_TIME_AUTHORIZATION_CONFIRMATION")}
        texts={[t("MY_TIME_AUTHORIZATION_CONFIRMATION_INFO")]}
        okButtonText={t("OC_ALL_RIGHT")}
        cancelButtonText={t("OC_CANCEL")}
      />
      {showViewingDialog && selectedUserRow && (
        <MyTimeUserApprovalDialog
          list={viewingData || []}
          loading={viewingLoading}
          allTimesNull={false}
          isExaminerMode={true}
          open={showViewingDialog}
          selectedUser={selectedUserRow}
          examinerTableDef={selectedTableDef}
          canExaminerApprove={canExaminerApprove}
          onSelectRow={originalonSelectRow}
          mutationLoading={createApprovalLoading || removeApprovalLoading || createAuthorizationLoading}
          onClose={() => {
            setShowViewingDialog(false);
            setViewingContractCode("");
            setSelectedUserRow(null);
          }}
        />
      )}
      <OcPageNameTitle title={t("MY_TIME_DASHBOARD")} goBack={false} />
      <Box padding="0px 16px">
        <MyTimePageHeader
          formattedDate={formattedDate}
          date={date}
          onDateChange={() => { }}
          isMobile={isMobile}
          closingperiodPage
        />
        <Box padding="16px 0px">
          <OcCard
            label={t("OC_MY_ROLES_EXAMINEES")}
            loading={loading || createApprovalLoading || removeApprovalLoading || createAllApprovalLoading || createAuthorizationLoading || createAllAuthorizationLoading}
            error={!!error}
          >
            <Box height="16px" />
            <MyTimeExaminerDahshboardRoleFilter
              loadQuery={loadQuery}
              loading={false}
              usersCount={list?.length || 0}
              roleFilterId={roleFilterId}
            />
            {(approveData === "true" || examinerApprovalData === "true") ? (
              <Box
                display="flex"
                flexDirection={isMobile ? "column" : "row"}
                justifyContent="flex-end"
                padding="32px 0px"
              >
                {approveData === "true" && (
                  <>
                    <Button
                      id="force-approval-button"
                      variant="outlined"
                      color="secondary"
                      startIcon={<InlineIcon icon="mdi:lock-outline" />}
                      onClick={() => setShowConfirmDialog(true)}
                      disabled={disabledForceButton}
                    >
                      {t("MY_TIME_FORCE_APPROVAL")}
                    </Button>
                    {examinerApprovalData === "true" && (
                      <>
                        <Box width="16px" />
                        {isMobile && <Box height="16px" />}
                      </>
                    )}
                  </>
                )}
                {examinerApprovalData === "true" && (
                  <Button
                    id='force-authorization-button'
                    variant="outlined"
                    color="secondary"
                    startIcon={<InlineIcon icon={checkAll} />}
                    onClick={() => setShowAuthorizationConfirmDialog(true)}
                    disabled={disabledForceAuthorizationButton}
                  >
                    {t("MY_TIME_FORCE_AUTHORIZATION")}
                  </Button>
                )}
              </Box>
            ) : (
              <Box height="32px" />
            )}
            <Box>
              <OcDataTable
                data={data}
                loading={loading}
                localPaging
                pagination
                onSelectRow={handleSelectRow}
                selectedRowId={selectedRowId}
                setTableData={handleTableDataChange}
                tableData={tableData}
                tableDef={tableDef}
                tableId={tableId}
                filteredTableDef={filteredTableDef}
                handleMoreButton={originalonSelectRow}
              />
            </Box>
          </OcCard>
        </Box>
      </Box>
    </Box>
  );
};

export default MyTimeExaminerDashboardPage;
